import { Globe2, Ship, Anchor, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type {
  DeliveryMethod,
  LogisticsIncoterm,
  Money,
} from '~/graphql/graphql';
import { DeliveryJourneyDiagram } from '~/components/common/DeliveryJourneyDiagram';
import { incotermRequiresAddress, useIncotermLabel } from '~/utils/incoterms';
import { deliveryMethodLabelKey } from '~/utils/deliveryMethod';
import { formatMoney } from '~/utils';

type PortLite = {
  id: string;
  name: string;
  city?: string | null;
  country?: string | null;
};

type RouteSnapshotLite = {
  id: string;
  forwarder?: string | null;
  transitDays?: number | null;
  freightCost?: Money | null;
  terminalHandlingCharge?: Money | null;
  gassingCost?: Money | null;
  additionalCostsPerContainer?: Money | null;
  additionalCostsPerShipment?: Money | null;
};

type OrderForPanel = {
  deliveryMethod?: DeliveryMethod | null;
  incotermLoading?: LogisticsIncoterm | null;
  incotermDischarge?: LogisticsIncoterm | null;
  incotermAddressDestination?: string | null;
  portOfLoading?: PortLite | null;
  portOfDischarge?: PortLite | null;
  shippingRoute?: { id: string; forwarder?: string | null } | null;
  shippingRouteSnapshot?: RouteSnapshotLite | null;
};

/** Read-only display of an international order's shipping selections + frozen route snapshot. */
export function OrderInternationalShippingCard({
  order,
}: Readonly<{ order: OrderForPanel }>) {
  const { t } = useTranslation(['cart', 'order']);
  const incotermLabel = useIncotermLabel();

  const portLabel = (port: PortLite | null | undefined) => {
    if (!port) return '—';
    const country = port.country ?? '';
    const city = port.city ?? '';
    return [port.name, [city, country].filter(Boolean).join(', ')]
      .filter(Boolean)
      .join(' · ');
  };

  const snapshot = order.shippingRouteSnapshot;

  return (
    <section
      className="bg-neut-0 border border-prim-100 rounded-lg p-4 flex flex-col gap-3"
      data-testid="order-international-shipping-card"
    >
      <h4 className="text-sm font-medium text-neut-prim flex items-center gap-1.5">
        <Globe2 className="w-4 h-4 text-prim-600" />
        {t('cart.international.title')}
      </h4>

      {order.deliveryMethod && (
        <div className="space-y-2">
          <Row
            label={t('cart.international.delivery_terms')}
            value={t(deliveryMethodLabelKey(order.deliveryMethod))}
          />
          <DeliveryJourneyDiagram method={order.deliveryMethod} />
        </div>
      )}

      <div className="space-y-1.5">
        <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-neut-sec">
          <Ship className="w-3 h-3 text-prim-600" />
          {t('cart.international.loading_side')}
        </div>
        <dl className="space-y-1.5 text-sm">
          <Row
            label={t('cart.international.incoterm_loading')}
            value={incotermLabel(order.incotermLoading)}
          />
          <Row
            label={t('cart.international.port_of_loading')}
            value={portLabel(order.portOfLoading)}
          />
        </dl>
      </div>

      <div className="border-t border-neut-100 pt-3 space-y-1.5">
        <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-neut-sec">
          <Anchor className="w-3 h-3 text-prim-600" />
          {t('cart.international.discharge_side')}
        </div>
        <dl className="space-y-1.5 text-sm">
          <Row
            label={t('cart.international.incoterm_discharge')}
            value={incotermLabel(order.incotermDischarge)}
          />
          <Row
            label={t('cart.international.port_of_discharge')}
            value={portLabel(order.portOfDischarge)}
          />
          {incotermRequiresAddress(order.incotermDischarge) &&
            order.incotermAddressDestination && (
              <Row
                label={t('cart.international.delivery_address')}
                value={order.incotermAddressDestination}
              />
            )}
        </dl>
      </div>

      {(snapshot ?? order.shippingRoute) && (
        <div className="border-t border-neut-100 pt-3 space-y-1.5">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-neut-sec">
            <Clock className="w-3 h-3 text-prim-600" />
            {t('cart.international.shipping_route')}
          </div>
          {snapshot ? (
            <dl className="space-y-1.5 text-sm">
              <Row
                label={t('order.international.forwarder', { ns: 'order' })}
                value={snapshot.forwarder ?? '—'}
              />
              <Row
                label={t('order.international.transit_days', { ns: 'order' })}
                value={
                  snapshot.transitDays != null
                    ? t('cart.international.transit_days', {
                        days: snapshot.transitDays,
                      })
                    : '—'
                }
              />
              {(
                [
                  ['freight_cost', snapshot.freightCost],
                  ['thc', snapshot.terminalHandlingCharge],
                  ['gassing', snapshot.gassingCost],
                  ['per_container', snapshot.additionalCostsPerContainer],
                  ['per_shipment', snapshot.additionalCostsPerShipment],
                ] as const
              ).map(([key, money]) => {
                const value = formatMoney(money);
                if (!value) return null;
                return (
                  <Row
                    key={key}
                    label={t(`order.international.${key}` as const, {
                      ns: 'order',
                    })}
                    value={value}
                  />
                );
              })}
            </dl>
          ) : (
            <p className="text-xs text-neut-sec italic">
              {t('order.international.snapshot_pending', { ns: 'order' })}
            </p>
          )}
        </div>
      )}
    </section>
  );
}

function Row({
  label,
  value,
}: Readonly<{
  label: string;
  value: string;
}>) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="text-xs text-neut-sec shrink-0">{label}</dt>
      <dd className="text-sm text-neut-prim font-medium truncate">{value}</dd>
    </div>
  );
}
